import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { FIELD_TYPE_SUGGESTIONS, isUnionKey } from "./helpers/fetcherSettingsResolver";
import {
  buildParam,
  EMPTY_PARAM_JSON,
  isStructuralJson,
  PARAM_MODES,
  ParsedParam,
  parseParam,
} from "./helpers/paramBuilder";

export interface ObjectDraft {
  /** id of the row being edited; absent when adding */
  id?: string;
  k: string;
  v: string;
  /** true while the guided JSON builder is open */
  builder: boolean;
}

interface Props {
  draft: ObjectDraft;
  onChange: (draft: ObjectDraft) => void;
  onCommit: () => void;
  onCancel: () => void;
}

/**
 * The inline add/edit form for a single `objects` row. field_value is free
 * text by default; the guided builder appears for the placeholder-JSON shape
 * the request-shaping keys use.
 */
const ObjectRowForm = ({ draft, onChange, onCommit, onCancel }: Props) => {
  const parsed = draft.builder ? parseParam(draft.v) : null;
  const structural = !draft.builder && isStructuralJson(draft.v);

  const patchParam = (patch: Partial<ParsedParam>) => {
    const current = parseParam(draft.v);
    if (!current) return;
    onChange({ ...draft, v: buildParam({ ...current, ...patch }) });
  };

  const openBuilder = () => {
    const value = parseParam(draft.v) ? draft.v : EMPTY_PARAM_JSON;
    onChange({ ...draft, v: value, builder: true });
  };

  return (
    <Box
      sx={{
        border: "1px solid #9C27B0",
        borderRadius: "8px",
        p: 2,
        mb: 1.5,
        backgroundColor: "#FBF7FC",
      }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Autocomplete
          freeSolo
          size="small"
          sx={{ width: 260 }}
          options={FIELD_TYPE_SUGGESTIONS}
          inputValue={draft.k}
          onInputChange={(_, value) => onChange({ ...draft, k: value })}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="field_type"
              placeholder="e.g. url_param"
              color="secondary"
            />
          )}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {!draft.builder ? (
            <>
              <TextField
                fullWidth
                multiline
                minRows={1}
                size="small"
                label="field_value"
                placeholder="plain text or JSON"
                value={draft.v}
                onChange={(event) =>
                  onChange({ ...draft, v: event.target.value })
                }
                color="secondary"
              />
              {structural ? (
                <Typography
                  sx={{ fontSize: "12px", color: "#00000099", mt: 0.5 }}
                >
                  Raw JSON — a nested structure the guided builder cannot model.
                </Typography>
              ) : (
                <Button size="small" color="secondary" onClick={openBuilder}>
                  {"{ } Build as placeholder JSON"}
                </Button>
              )}
            </>
          ) : (
            parsed && (
              <Stack spacing={1.5}>
                {(parsed.mode !== "widget" || parsed.hasName) && (
                  <TextField
                    size="small"
                    label="#name"
                    value={parsed.name}
                    onChange={(event) =>
                      patchParam({ name: event.target.value })
                    }
                    color="secondary"
                  />
                )}

                <TextField
                  select
                  size="small"
                  label="Shape"
                  value={parsed.mode}
                  onChange={(event) =>
                    patchParam({ mode: event.target.value as any })
                  }
                  color="secondary"
                >
                  {PARAM_MODES.map((mode) => (
                    <MenuItem key={mode.value} value={mode.value}>
                      {mode.label}
                    </MenuItem>
                  ))}
                </TextField>

                {parsed.mode === "value" && (
                  <TextField
                    size="small"
                    label="#value"
                    value={parsed.val}
                    onChange={(event) => patchParam({ val: event.target.value })}
                    color="secondary"
                  />
                )}

                {parsed.mode === "values" &&
                  parsed.values.map((entry, index) => (
                    <Stack key={index} direction="row" spacing={1}>
                      <TextField
                        fullWidth
                        size="small"
                        label={index === 0 ? "#values" : " "}
                        value={entry}
                        onChange={(event) => {
                          const values = [...parsed.values];
                          values[index] = event.target.value;
                          patchParam({ values });
                        }}
                        color="secondary"
                      />
                      <IconButton
                        size="small"
                        onClick={() => {
                          const values = parsed.values.filter(
                            (_, position) => position !== index,
                          );
                          patchParam({ values: values.length ? values : [""] });
                        }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                {parsed.mode === "values" && (
                  <Button
                    size="small"
                    color="secondary"
                    startIcon={<AddIcon />}
                    onClick={() => patchParam({ values: [...parsed.values, ""] })}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    Add value
                  </Button>
                )}

                {parsed.mode === "keeper" && (
                  <>
                    <TextField
                      size="small"
                      label="#keeper_name"
                      value={parsed.keeper}
                      onChange={(event) =>
                        patchParam({ keeper: event.target.value })
                      }
                      color="secondary"
                    />
                    <TextField
                      size="small"
                      label="#kind"
                      placeholder="optional - e.g. Bearer"
                      value={parsed.kind}
                      onChange={(event) =>
                        patchParam({ kind: event.target.value })
                      }
                      color="secondary"
                    />
                  </>
                )}

                {parsed.mode === "user_input" && (
                  <>
                    <TextField
                      size="small"
                      label="#input_name"
                      value={parsed.input}
                      onChange={(event) =>
                        patchParam({ input: event.target.value })
                      }
                      color="secondary"
                    />
                    <TextField
                      size="small"
                      label="#format"
                      value={parsed.fmt}
                      onChange={(event) =>
                        patchParam({ fmt: event.target.value })
                      }
                      color="secondary"
                    />
                  </>
                )}

                {parsed.mode === "view" && (
                  <TextField
                    size="small"
                    label="#table_field"
                    value={parsed.field}
                    onChange={(event) =>
                      patchParam({ field: event.target.value })
                    }
                    color="secondary"
                  />
                )}

                {parsed.mode === "dateformat" && (
                  <TextField
                    size="small"
                    label="#dateformat"
                    value={parsed.dateformat}
                    onChange={(event) =>
                      patchParam({ dateformat: event.target.value })
                    }
                    color="secondary"
                  />
                )}

                {parsed.mode === "widget" && (
                  <>
                    <TextField
                      size="small"
                      label="#type"
                      placeholder="ID / XPATH / CLASS"
                      value={parsed.type}
                      onChange={(event) =>
                        patchParam({ type: event.target.value })
                      }
                      color="secondary"
                    />
                    <TextField
                      size="small"
                      label="#value"
                      value={parsed.val}
                      onChange={(event) =>
                        patchParam({ val: event.target.value })
                      }
                      color="secondary"
                    />
                    <TextField
                      size="small"
                      label="#text"
                      value={parsed.text}
                      onChange={(event) =>
                        patchParam({ text: event.target.value })
                      }
                      color="secondary"
                    />
                  </>
                )}

                {Object.keys(parsed.extra).length > 0 && (
                  <Typography sx={{ fontSize: "12px", color: "#00000099" }}>
                    {Object.keys(parsed.extra).join(", ")} preserved as-is.
                  </Typography>
                )}

                <Box
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "12px",
                    backgroundColor: "#F0F0F3",
                    borderRadius: "6px",
                    p: 1,
                    overflowX: "auto",
                  }}
                >
                  {draft.v}
                </Box>

                <Button
                  size="small"
                  color="secondary"
                  sx={{ alignSelf: "flex-start" }}
                  onClick={() => onChange({ ...draft, builder: false })}
                >
                  Back to plain text
                </Button>
              </Stack>
            )
          )}
        </Box>
      </Stack>

      {draft.k && (
        <Typography sx={{ fontSize: "12px", color: "#00000099", mt: 1 }}>
          {isUnionKey(draft.k)
            ? `${draft.k} is combined across levels — this value is added to any set on the interface or the brand, not instead of them.`
            : `${draft.k} follows the normal rule — a value set on the brand or the raw table replaces this one entirely.`}
        </Typography>
      )}

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
        <Button size="small" color="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          color="secondary"
          disabled={!draft.k}
          onClick={onCommit}
        >
          {draft.id ? "Apply" : "Add"}
        </Button>
      </Stack>
    </Box>
  );
};

export default ObjectRowForm;
